export type FeatureTourRole = "student" | "tutor" | "advertiser";

export type FeatureTourStep = {
  id: string;
  /** Matches `[data-tour="..."]` on nav / bottom-nav / hamburger links */
  target: string;
  /** Open the mobile hamburger sheet so this target is visible */
  mobileSheet?: boolean;
  title: string;
  body: string;
};

export const TOUR_TARGET_BY_HREF: Record<string, string> = {
  "/student": "nav-student",
  "/feed": "nav-feed",
  "/certifications": "nav-certifications",
  "/free-study-planner": "nav-study-planner",
  "/forums": "nav-forums",
  "/tutors": "nav-tutors",
  "/study-groups": "nav-study-groups",
  "/videos": "nav-videos",
  "/tutor": "nav-tutor",
  "/advertiser/dashboard": "nav-advertiser",
};

export function tourTargetForHref(href: string): string | undefined {
  return TOUR_TARGET_BY_HREF[href];
}

export const FEATURE_TOUR_SKIP_PATHS = [
  "/onboarding",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/tutor/onboarding",
  "/tutor/apply",
  "/tutor/setup",
  "/tutor/create-account",
  "/tutor/rejected",
  "/founding-beta",
];

export const OPEN_MOBILE_MENU_EVENT = "certlynx:open-mobile-menu";
export const CLOSE_MOBILE_MENU_EVENT = "certlynx:close-mobile-menu";

export const FEATURE_TOUR_STEPS: Record<FeatureTourRole, FeatureTourStep[]> = {
  student: [
    {
      id: "student",
      target: "nav-student",
      title: "Student Dashboard",
      body: "Your goals, sessions, and study plans live here. Open it anytime you want a snapshot of your progress.",
    },
    {
      id: "feed",
      target: "nav-feed",
      title: "Feed",
      body: "Celebrate wins and watch study videos from people on the same path. Post an update when something clicks.",
    },
    {
      id: "forums",
      target: "nav-forums",
      title: "Forums",
      body: "Stuck on a topic? Ask the community. Follow threads for your cert so answers find you.",
    },
    {
      id: "study-groups",
      target: "nav-study-groups",
      mobileSheet: true,
      title: "Study Groups",
      body: "Study with people chasing the same cert. Join a group and show up to a session.",
    },
    {
      id: "videos",
      target: "nav-videos",
      title: "Video Studio",
      body: "Record a short walkthrough of what you just learned. Teaching it is the fastest way to lock it in.",
    },
  ],
  tutor: [
    {
      id: "tutor",
      target: "nav-tutor",
      title: "Tutor Dashboard",
      body: "Bookings, availability, and earnings all land here. Check it first when you sign in.",
    },
    {
      id: "feed",
      target: "nav-feed",
      title: "Feed",
      body: "Share a tip or a win so students can find you. A short post goes a long way.",
    },
    {
      id: "forums",
      target: "nav-forums",
      title: "Forums",
      body: "Answer a question in your specialty. Students notice the tutors who show up.",
    },
    {
      id: "videos",
      target: "nav-videos",
      title: "Video Studio",
      body: "Post a teaching clip students can watch before they book. It’s the fastest way to build trust.",
    },
  ],
  advertiser: [
    {
      id: "advertiser",
      target: "nav-advertiser",
      mobileSheet: true,
      title: "Advertiser Dashboard",
      body: "Create a campaign, watch performance, and manage billing from one place. Start here every time.",
    },
  ],
};

export const SOCIAL_NUDGE_WINDOW_MS = 48 * 60 * 60 * 1000;
